import greenCoin from '@/assets/branding/green-coin.png';
import MarketHandler from '@/components/handlers/market-handler';
import WebSocketHandler from '@/components/handlers/ws-handler';
import LeverageModal from '@/components/header/modals/leverage-modal';
import { Button } from '@/components/ui';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import Activity from '@/features/markets/components/activity';
import TradeTabs from '@/features/markets/components/trade-tabs';
import LazyTradingView from '@/features/trade/components/lazy-tv-chart';
import MarketInfo from '@/features/trade/components/market-info';
import MarketsDropdown from '@/features/trade/components/markets-dropdown';
import ReduceOnlyBanner from '@/features/trade/components/reduce-only-banner';
import OrderForm from '@/features/trade/order/components/order-form';
import useSymbol from '@/hooks/use-symbol';
import useViewport from '@/hooks/use-viewport';
import { cn } from '@/lib/utils';
import { useMarketStore } from '@/store/use-markets-store';
import { EventType, MarketStatus } from '@/types';
import { sendEvent } from '@/utils/analytics';
import { abbrFromSym } from '@/utils/token-symbol';
import { useState } from 'react';
import { useStore } from 'zustand';

export default function TradePage() {
  const viewport = useViewport();
  const symbol = useSymbol();
  const isHidden = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.status === MarketStatus.HALT,
  );

  return (
    <>
      <WebSocketHandler />
      <MarketHandler />
      <div className="flex h-full flex-col">
        <div className="relative flex h-16 shrink-0 border-b border-border bg-background sm:h-20">
          <div className="flex flex-1 md:grid-cols-[320px_1fr]">
            <MarketsDropdown />
            <MarketInfo />
          </div>
          {!isHidden && <LeverageModal />}
        </div>
        <ReduceOnlyBanner />
        <div className="relative flex flex-1 flex-col md:flex-row">
          <div className="flex max-h-[228px] min-h-[200px] flex-1 flex-col md:max-h-none md:min-h-0">
            <ResizablePanelGroup direction="vertical">
              <ResizablePanel className="lg:min-h-48">
                <div className="flex h-full">
                  <LazyTradingView />
                  {!isHidden && viewport === 'lg' && <Activity />}
                </div>
              </ResizablePanel>
              <ResizableHandle withHandle />
              {viewport === 'lg' && (
                <ResizablePanel minSize={10} defaultSize={30.5}>
                  <TradeTabs />
                </ResizablePanel>
              )}
            </ResizablePanelGroup>
          </div>
          <div
            className={cn(
              'flex w-full flex-1 flex-col border-border bg-background transition-opacity md:max-w-md md:flex-none md:border-l',
            )}
          >
            {isHidden ? <UnlistedMarket /> : <OrderForm />}
          </div>
        </div>
      </div>
    </>
  );
}

function UnlistedMarket() {
  const [isRequested, setIsRequested] = useState(false);
  const symbol = useSymbol();
  return (
    <div className="flex h-full w-full flex-col items-center gap-2 px-6 py-12 md:px-7 md:py-14">
      <img className="max-w-40" src={greenCoin} alt="Green Coin" />
      <h3 className="mt-6 text-2xl font-semibold">
        You want it? We'll get it.
      </h3>
      <p className="text-center text-lg text-muted-foreground sm:text-xl">
        {abbrFromSym(symbol)} is not listed on Vest.
        <br />
        Request it today!
      </p>
      <Button
        variant="foreground"
        size="xl"
        className="mt-6 w-full"
        disabled={isRequested}
        onClick={() => {
          sendEvent(EventType.UNLISTED_MARKET_REQUESTED, { symbol });
          setIsRequested(true);
        }}
      >
        {isRequested
          ? `${abbrFromSym(symbol)} Requested for Listing`
          : `Request ${abbrFromSym(symbol)} Listing`}
      </Button>
    </div>
  );
}
